import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import LoginPage from './components/LoginPage';
import GameRoom from './components/GameRoom';
import ConfigPage from './components/ConfigPage';

function App() {
  const [currentView, setCurrentView] = useState('login');
  const [user, setUser] = useState(null);
  const [room, setRoom] = useState(null);

  useEffect(() => {
    // 检查是否已经登录
    if (supabase) {
      const savedUser = localStorage.getItem('poker_user');
      const savedRoom = localStorage.getItem('poker_room');
      
      if (savedUser && savedRoom) {
        setUser(JSON.parse(savedUser));
        setRoom(JSON.parse(savedRoom));
        setCurrentView('game');
      }
    }
  }, []);

  // 检查 Supabase 是否已配置
  if (!supabase) {
    return <ConfigPage />;
  }

  const handleLogin = async (roomKey, username) => {
    try {
      // 检查房间是否存在
      const { data: existingRoom, error: roomError } = await supabase
        .from('rooms')
        .select('*')
        .eq('room_key', roomKey)
        .single();

      let currentRoom;
      
      if (roomError && roomError.code === 'PGRST116') {
        // 房间不存在，创建新房间
        const { data: newRoom, error: createError } = await supabase
          .from('rooms')
          .insert([{
            room_key: roomKey,
            room_name: `房间 ${roomKey}`,
            max_players: 6,
            current_players: 0,
            status: 'waiting'
          }])
          .select()
          .single();

        if (createError) {
          throw createError;
        }
        currentRoom = newRoom;
      } else if (roomError) {
        throw roomError;
      } else {
        currentRoom = existingRoom;
      }

      // 检查房间是否已满
      if (currentRoom.current_players >= currentRoom.max_players) {
        alert('房间已满，请选择其他房间');
        return;
      }

      // 检查用户名是否已存在
      const { data: existingPlayer } = await supabase
        .from('players')
        .select('*')
        .eq('room_id', currentRoom.id)
        .eq('player_name', username);

      if (existingPlayer && existingPlayer.length > 0) {
        alert('用户名已存在，请选择其他用户名');
        return;
      }

      // 创建玩家
      const { data: newPlayer, error: playerError } = await supabase
        .from('players')
        .insert([{
          room_id: currentRoom.id,
          player_name: username,
          chips: 1000,
          is_host: currentRoom.current_players === 0, // 第一个玩家是房主
          seat_position: currentRoom.current_players + 1
        }])
        .select()
        .single();

      if (playerError) {
        throw playerError;
      }

      // 更新房间玩家数量
      await supabase
        .from('rooms')
        .update({
          current_players: currentRoom.current_players + 1,
          host_id: currentRoom.current_players === 0 ? newPlayer.id : currentRoom.host_id
        })
        .eq('id', currentRoom.id);

      // 保存用户信息
      const userInfo = {
        id: newPlayer.id,
        username: username,
        chips: 1000,
        isHost: newPlayer.is_host,
        roomId: currentRoom.id
      };

      const roomInfo = {
        id: currentRoom.id,
        key: roomKey,
        name: currentRoom.room_name
      };

      setUser(userInfo);
      setRoom(roomInfo);
      
      // 保存到本地存储
      localStorage.setItem('poker_user', JSON.stringify(userInfo));
      localStorage.setItem('poker_room', JSON.stringify(roomInfo));
      
      setCurrentView('game');
    } catch (error) {
      console.error('登录失败:', error);
      alert('登录失败，请重试');
    }
  };

  const handleLogout = async () => {
    try {
      if (user && room) {
        // 检查当前房间状态
        const { data: roomData } = await supabase
          .from('rooms')
          .select('current_players, host_id')
          .eq('id', room.id)
          .single();

        // 检查房间内其他玩家
        const { data: otherPlayers } = await supabase
          .from('players')
          .select('*')
          .eq('room_id', room.id)
          .neq('id', user.id);

        // 从数据库中删除当前玩家
        await supabase
          .from('players')
          .delete()
          .eq('id', user.id);

        if (roomData) {
          const newPlayerCount = Math.max(0, roomData.current_players - 1);
          
          if (newPlayerCount === 0) {
            // 房间空了，删除房间
            await supabase
              .from('rooms')
              .delete()
              .eq('id', room.id);
            
            console.log('房间已清空并删除');
          } else if (user.isHost && otherPlayers && otherPlayers.length > 0) {
            // 如果退出的是房主，需要转移房主身份
            const newHost = otherPlayers[0]; // 选择第一个其他玩家作为新房主
            
            // 更新新房主
            await supabase
              .from('players')
              .update({ is_host: true })
              .eq('id', newHost.id);
            
            // 更新房间信息
            await supabase
              .from('rooms')
              .update({
                current_players: newPlayerCount,
                host_id: newHost.id
              })
              .eq('id', room.id);
            
            console.log('房主身份已转移至:', newHost.player_name);
          } else {
            // 普通玩家退出，只更新玩家数量
            await supabase
              .from('rooms')
              .update({
                current_players: newPlayerCount
              })
              .eq('id', room.id);
          }
        }
      }

      // 清除本地存储
      localStorage.removeItem('poker_user');
      localStorage.removeItem('poker_room');
      
      setUser(null);
      setRoom(null);
      setCurrentView('login');
    } catch (error) {
      console.error('退出失败:', error);
    }
  };

  if (currentView === 'login') {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <GameRoom 
      user={user} 
      room={room} 
      onLogout={handleLogout}
    />
  );
}

export default App; 